import { Timestamp } from '@angular/fire/firestore';
import { Auditable, Dictionary, Dto, IDName } from '@damofire/data-commons';
import { ProductColor } from '@damofire/data-repos';
import { PartialOrganization } from '@damofire/data-stas';

export interface ProductionItem extends Dto<string>, Auditable {
  archived?: boolean;
  job?: string;
  po?: string;
  installer?: IDName<string>;
  installationPo?: string;
  installationPoValue?: string;
  installationDate?: Timestamp;
  installedDate?: Timestamp;
  distributor?: IDName<string>;
  customer?: IDName<string>;
  step?: string;
  description?: string;
  suppliers?: Dictionary<Supplier>;
  eu?: number;
  expedition?: Timestamp;
  expeditionLimit?: Timestamp;
  expeditionLimitPriority?: string;
  expeditionHighWarningDate?: Timestamp;
  expeditionMediumWarningDate?: Timestamp;
  shipsWith?: number[];
  readyForProduction?: boolean;
  needsHands?: boolean;
  poSent?: boolean;
  stock?: boolean;
  colors?: ProductColor[];
  drawingPath?: string;
  stepProductionDate?: Timestamp;
  shipper?: Shipper;
  language?: string;
  notes?: string;
  jobValue?: number;
}

export interface Supplier {
  _id?: string;
  name: string;
  po?: string;
  ready: boolean;
}

export interface ShippingInfo extends Auditable {
  address?: string;
  collect?: boolean;
  contact?: string;
  description?: string;
  po?: string;
  prepaid?: boolean;
  pro?: string;
  shipper?: Shipper;
  skids?: number;
}

export interface Shipper {
  _id?: string;
  name: string;
}

export interface Message extends Dto<string> {
  account?: string;
  enabled?: boolean;
  addresses?: string[];
  history?: History[];
  template?: string;
  title?: string;
  sender?: string;
  message?: string;
}

export interface History {
  status?: string;
  when?: Timestamp;
}

export interface ProductionProduct extends Dto<string> {
  name?: string;
  description?: string;
  supplier?: PartialOrganization;
  eu?: number;
  enabled?: boolean;
  quantity: number;
  ready?: boolean;
  po?: string;
}

export interface ProductionMaterial extends Dto {
  description?: string;
  filename?: string;
  productionItemId?: number;
  job?: string;
  qty?: number;
  section?: string;
  suspect?: boolean;
  color?: string;
  colorName?: string;
  index?: number;
  dirty?: boolean;
}

export interface ProductionProgressionItem {
  progressionItems: string[];
  createdBy: string;
}

export enum PreProdSteps {
  ACK = 'ACK',
  NEW = 'NEW',
  DRAWING = 'DRAWING',
  PREMAN = 'PREMAN',
}

export enum ProdSteps {
  PREMAN = 'PREMAN',
  MANUFACTURING = 'MANUFACTURING',
  ROBOT = 'ROBOT',
  ASSEMBLY = 'ASSEMBLY',
  CLEANING = 'CLEANING',
  PAINTING = 'PAINTING',
  PACKAGING = 'PACKAGING',
  SHIPPING = 'SHIPPING',
}

export enum PostProdSteps {
  SHIPPING = 'SHIPPING',
  SHIPPED = 'SHIPPED',
  INSTALLATION = 'INSTALLATION',
  INSTALLED = 'INSTALLED',
}

export const FORM_CONTROLS = {
  jobNumber: 'jobNumber',
  customer: 'customer',
  customerContacts: 'customerContacts',
  distributor: 'distributor',
  distributorContacts: 'distributorContacts',
  installer: 'installer',
  shipper: 'shipper',
};

export const AllProdSteps = {
  ...PreProdSteps,
  ...ProdSteps,
  ...PostProdSteps,
};

export const ProductionItemPriority = {
  urgent: 5,
  high: 4,
  normal: 3,
  low: 2,
  inactive: 1,
};

export const ValidationKeys = [
  { column: PreProdSteps.ACK, restriction: '', stepIndex: 0 },
  { column: PreProdSteps.NEW, restriction: '', stepIndex: 1 },
  { column: PreProdSteps.DRAWING, restriction: '', stepIndex: 2 },
  { column: PreProdSteps.PREMAN, restriction: 'description', stepIndex: 3, errorKey: 'products' },
  { column: ProdSteps.MANUFACTURING, restriction: 'description', stepIndex: 4, errorKey: 'products' },
  { column: ProdSteps.ROBOT, restriction: 'description', stepIndex: 5, errorKey: 'products' },
  { column: ProdSteps.ASSEMBLY, restriction: 'description', stepIndex: 6, errorKey: 'products' },
  { column: ProdSteps.CLEANING, restriction: 'description', stepIndex: 7, errorKey: 'products' },
  { column: ProdSteps.PAINTING, restriction: 'description', stepIndex: 8, errorKey: 'products' },
  { column: ProdSteps.PACKAGING, restriction: 'description', stepIndex: 9, errorKey: 'products' },
  { column: PostProdSteps.SHIPPING, restriction: 'description', stepIndex: 10, errorKey: 'products' },
  { column: PostProdSteps.SHIPPED, restriction: 'shipper', stepIndex: 11, errorKey: 'shipper' },
  { column: PostProdSteps.INSTALLATION, restriction: 'installer', stepIndex: 12, errorKey: 'installer' },
  { column: PostProdSteps.INSTALLED, restriction: 'installer', stepIndex: 13, errorKey: 'installer' },
];

export interface Contact extends Dto<string>, Auditable {
  email?: string;
  displayName?: string;
  divisionId?: string;
}
